/**
 * Freshness helpers
 *
 * Small utilities for displaying "last updated" timestamps and deciding
 * whether content is stale relative to a reference timestamp.
 *
 * Used by AlternateFilesPanel to surface alternate-content-file freshness
 * (last generated timestamp, version chip, "Just refreshed" pulse, "Stale"
 * indicator).
 */

import { formatDistanceToNow, parseISO } from 'date-fns'

const ONE_DAY_MS = 24 * 60 * 60 * 1000

function toDate(input: string | Date | null | undefined): Date | null {
  if (!input) return null
  if (input instanceof Date) return input
  try {
    return parseISO(input)
  } catch {
    return null
  }
}

/**
 * Has this timestamp happened within the last `windowMs` milliseconds?
 * Used to power the temporary "Just refreshed" pulse pill.
 */
export function isJustRefreshed(
  iso: string | Date | null | undefined,
  windowMs = 30_000
): boolean {
  const d = toDate(iso)
  if (!d) return false
  return Date.now() - d.getTime() < windowMs
}

/**
 * Should we show a "Stale" indicator for this content?
 *
 * Considered stale when EITHER:
 *   - the schema was regenerated after the AI-readable files
 *     (schemaUpdatedAt > altUpdatedAt), OR
 *   - the AI-readable files are older than `maxAgeDays` regardless
 */
export function isStale(
  altUpdatedAt: string | Date | null | undefined,
  schemaUpdatedAt?: string | Date | null,
  maxAgeDays = 30
): boolean {
  const alt = toDate(altUpdatedAt)
  if (!alt) return false // No timestamp → don't editorialize

  const schema = toDate(schemaUpdatedAt)
  if (schema && schema.getTime() > alt.getTime()) return true

  const ageMs = Date.now() - alt.getTime()
  if (ageMs > maxAgeDays * ONE_DAY_MS) return true

  return false
}

/**
 * Human-readable relative time. "2 minutes ago", "3 days ago", etc.
 * Returns null for invalid input so the caller can decide what to render.
 */
export function formatRelativeTime(
  iso: string | Date | null | undefined
): string | null {
  const d = toDate(iso)
  if (!d) return null
  return formatDistanceToNow(d, { addSuffix: true })
}
